import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Alert, Box, Button, Card, CircularProgress, Divider, Grid, Pagination, Stack, Typography } from '@mui/material';
import { PermissionGuard } from '../PermissionGuard';
import InstructionInput from './InstructionInput';
import { InstructionService, instructionResponseOk as ok } from './Service';

const dateLabel = (value) => value && Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '';
function InstructionSection({ id, quickBook, type, value, canEdit, onSaved }) {
  const label = type === 'CUSTOMER' ? 'Customer Instructions' : 'CuDel Support Instructions';
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [page, setPage] = useState(1);
  const expectedVersion = useRef(0);
  const { control, reset, handleSubmit } = useForm({ defaultValues: { text: '' }, mode: 'onChange' });
  useEffect(() => {
    setEditing(false); setNotice(null); setConflict(false); setHistoryOpen(false); setHistory(null); setPage(1);
  }, [id, quickBook]);
  const loadHistory = useCallback(async (nextPage) => {
    setHistoryLoading(true); setHistoryError('');
    try {
      const response = await InstructionService.history(id, quickBook, type, nextPage);
      if (!ok(response)) { setHistoryError(response?.message || 'Unable to load history.'); return; }
      setHistory(response.data); setPage(nextPage);
    } catch { setHistoryError('Unable to load history. Try again.'); }
    finally { setHistoryLoading(false); }
  }, [id, quickBook, type]);
  const begin = () => { expectedVersion.current = value?.version || 0; reset({ text: value?.text || '' }); setNotice(null); setConflict(false); setEditing(true); };
  const save = handleSubmit(async ({ text }) => {
    setSaving(true); setNotice(null);
    try {
      const response = await InstructionService.update(id, quickBook, { instructionType: type, text: text.trim() ? text : null, expectedVersion: expectedVersion.current });
      if (!ok(response)) {
        if (response?._http?.status === 409) {
          setConflict(true);
          setNotice('Another update was saved. Your draft is preserved. Compare the latest saved text below, then explicitly keep your draft or cancel.');
          await onSaved();
        } else setNotice(response?.message || 'Unable to save instructions. Your draft is preserved.');
        return;
      }
      setEditing(false); setConflict(false); await onSaved();
      if (historyOpen) await loadHistory(1);
    } catch { setNotice('Unable to save instructions. Your draft is preserved. Try again.'); }
    finally { setSaving(false); }
  });
  return <Card variant="outlined" sx={{
    p: 2, height: '100%', minWidth: 0,
    '& .MuiTypography-root': { fontSize: '0.75rem' },
    '& .MuiTypography-subtitle2': { fontSize: '0.8rem', fontWeight: 600 },
    '& .MuiTypography-caption': { fontSize: '0.65rem' },
    '& .MuiButton-root, & .MuiPaginationItem-root': { fontSize: '0.7rem' },
    '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: '0.75rem' },
    '& .MuiFormHelperText-root': { fontSize: '0.65rem' },
    '& .MuiAlert-message': { fontSize: '0.75rem' },
  }}>
    <Stack spacing={1.5}>
      <Typography component="h3" variant="subtitle2">{label}</Typography>
      {type === 'SUPPORT' && <Typography variant="caption" color="text.secondary">Visible to CuDel Support and the assigned expert only.</Typography>}
      <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value?.text || 'No instructions added.'}</Typography>
      {value?.updatedAt && <Typography variant="caption" color="text.secondary">{value?.editedBySupport ? 'Edited by CuDel Support' : 'Updated'} · {dateLabel(value.updatedAt)}</Typography>}
      {notice && <Alert severity={conflict ? 'warning' : 'error'}>{notice}</Alert>}
      {editing ? <Box component="form" onSubmit={save}>
        <Stack spacing={1.5}>
          <InstructionInput control={control} label={`Edit ${label}`} disabled={saving || !canEdit} />
          {conflict && <Button variant="outlined" disabled={saving || !canEdit} onClick={() => { expectedVersion.current = value?.version || 0; setConflict(false); setNotice(null); }}>Keep my draft against the latest version</Button>}
          {!canEdit && <Alert severity="info">This booking no longer allows instruction edits.</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button type="submit" variant="contained" disabled={saving || conflict || !canEdit}>Save</Button>
            <Button disabled={saving} onClick={() => { setEditing(false); setNotice(null); setConflict(false); }}>Cancel</Button>
          </Stack>
        </Stack>
      </Box> : <PermissionGuard feature="booking" permission="canEdit">{canEdit && <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }} onClick={begin}>{value?.text ? 'Edit' : 'Add'} {label}</Button>}</PermissionGuard>}
      <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => { setHistoryOpen(!historyOpen); if (!historyOpen) loadHistory(1); }}>{historyOpen ? 'Hide history' : `View ${label} history`}</Button>
      {historyOpen && <Stack spacing={1}>
        <Divider />
        {historyLoading && <CircularProgress size={20} aria-label="Loading instruction history" />}
        {historyError && <Alert severity="error" action={<Button color="inherit" onClick={() => loadHistory(page)}>Retry</Button>}>{historyError}</Alert>}
        {!historyLoading && !historyError && !history?.items?.length && <Typography variant="body2">No instruction history.</Typography>}
        {history?.items?.map((item) => <Box key={item.id} sx={{ overflowWrap: 'anywhere' }}>
          <Typography variant="caption">Version {item.version} · {item.editedByRole} {item.editedByUserId ? `#${item.editedByUserId}` : ''} · {dateLabel(item.createdAt)}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>Previous: {item.previousText || '(empty)'}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>Saved: {item.updatedText || '(removed)'}</Typography>
          {item.changeReason && <Typography variant="caption">Reason: {item.changeReason}</Typography>}
          <Divider sx={{ mt: 1 }} />
        </Box>)}
        {history?.pagination?.totalPages > 1 && <Pagination count={history.pagination.totalPages} page={page} size="small" disabled={historyLoading} onChange={(_, next) => loadHistory(next)} />}
      </Stack>}
    </Stack>
  </Card>;
}
export default function BookingInstructions({ id, quickBook = false, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const loadId = useRef(0);
  const resource = useRef({ id, quickBook });
  resource.current = { id, quickBook };
  const load = useCallback(async () => {
    // A completed save for the previous route must not reload it into this page.
    if (resource.current.id !== id || resource.current.quickBook !== quickBook) return;
    const current = ++loadId.current;
    setLoading(true); setError('');
    try {
      const response = await InstructionService.latest(id, quickBook);
      if (current !== loadId.current) return;
      if (ok(response)) setData(response.data);
      else setError(response?.message || 'Unable to load instructions.');
    } catch { if (current === loadId.current) { setError('Unable to load instructions.'); } }
    finally { if (current === loadId.current) setLoading(false); }
  }, [id, quickBook]);
  useEffect(() => { setData(null); if (id) load(); return () => { loadId.current += 1; }; }, [id, load, refreshKey]);
  if (!id) return null;
  return <Box sx={{ my: 1.5 }}>
    {loading && <CircularProgress size={20} aria-label="Loading instructions" />}
    {error && <Alert severity="error" action={<Button color="inherit" onClick={load}>Retry</Button>}>{error}</Alert>}
    {data && <Grid container spacing={1.5}>{['CUSTOMER', 'SUPPORT'].map((type) => <Grid item xs={12} md={6} key={`${id}-${type}`}><InstructionSection id={id} quickBook={quickBook} type={type} value={data[type.toLowerCase()]} canEdit={data.canEdit === true && !error} onSaved={load} /></Grid>)}</Grid>}
  </Box>;
}
